import { useState } from 'react';
import { OctagonX } from 'lucide-react';
import { z } from 'zod';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormLabel
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { useCoaches } from '@/data/coach';

import {
  useCoachActivity,
  useUnApproveCoachActivity,
  useCoachActivities
} from '@/data/coachActivity';

const formSchema = z.object({
  isConfirmed: z.boolean().refine((val) => val === true, {
    message: 'Must confirm un-approval'
  })
});

const UnApproveDialog = ({ coachActivityId, setIsOpen }) => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const coaches = useCoaches();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      isConfirmed: false
    }
  });

  const params = useParams();
  const coachActivity = useCoachActivity(coachActivityId);
  const unApproveCoachActivity = useUnApproveCoachActivity();
  const coachActivities = useCoachActivities(params.id);

  const onSubmit = async () => {
    try {
      setSubmitting(true);
      setApiError('');

      // Un-approve the coach activity
      const updatedCoachActivity =
        await unApproveCoachActivity(coachActivityId);
      await coachActivity.mutate(updatedCoachActivity, false);
      await coaches.mutate();
      await coachActivities.mutate();
      setSubmitting(false);
      setIsOpen(false);
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive">
          <OctagonX className="mr-2 h-4 w-4" />
          Un-Approve
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Un-Approve Coach Activity</DialogTitle>
          <DialogDescription>
            Please confirm that you want to un-approve this coach activity. The
            coach will no longer be visible for this activity.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="isConfirmed"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>
                      I confirm I want to un-approve this coach activity
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>
                Un-Approve Activity
              </SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UnApproveDialog;
